import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Text,
  VStack,
  HStack,
  Heading,
  Button,
  Input,
  Textarea,
  Checkbox,
  Spinner,
  Badge,
  Divider,
} from '@chakra-ui/react';
import apiService, { handleApiError } from '../services/api';

const ET_TZ = 'America/New_York';

const fmtDateTime = (iso) =>
  iso
    ? new Date(iso).toLocaleString('en-US', {
        dateStyle: 'long',
        timeStyle: 'short',
        timeZone: ET_TZ,
      })
    : '';

const fmtDate = (iso) =>
  iso
    ? new Date(`${iso}T12:00:00`).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : '';

const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

// Body sections come from the Django admin's TinyMCE editors — our own staff
// authored content, same trust model as article and project bodies elsewhere
// on the site.
const Section = ({ title, html }) => {
  if (!html || !html.trim()) return null;
  return (
    <Box w="100%">
      <Heading size="sm" color="green.400" textTransform="uppercase" letterSpacing="wide" mb={3}>
        {title}
      </Heading>
      <Box
        color="gray.200"
        lineHeight="1.8"
        fontSize={{ base: 'sm', md: 'md' }}
        sx={{
          'ul, ol': { paddingLeft: '1.25rem', marginY: '0.5rem' },
          li: { marginBottom: '0.35rem' },
          p: { marginBottom: '0.75rem' },
          a: { color: '#3FA66A', textDecoration: 'underline' },
          strong: { color: 'white' },
        }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </Box>
  );
};

const Fact = ({ label, children }) => (
  <HStack align="start" spacing={4} w="100%">
    <Text color="gray.400" fontSize="sm" minW={{ base: '7rem', md: '9rem' }}>
      {label}
    </Text>
    <Text color="white" fontSize="sm" fontWeight="600">
      {children}
    </Text>
  </HStack>
);

const Shell = ({ children }) => (
  <Box py={{ base: 12, md: 20 }} px={4}>
    <VStack spacing={8} maxW="3xl" mx="auto">
      {children}
    </VStack>
  </Box>
);

const Notice = ({ emoji, title, children }) => (
  <Shell>
    <Box w={16} h={16} borderRadius="full" bg="gray.800" display="flex" alignItems="center" justifyContent="center" fontSize="3xl">
      {emoji}
    </Box>
    <Heading size={{ base: 'lg', md: 'xl' }} textAlign="center" textTransform="uppercase" letterSpacing="wide">
      {title}
    </Heading>
    <Text color="gray.300" textAlign="center" lineHeight="1.8" maxW="lg">
      {children}
    </Text>
    <Button as="a" href="mailto:hello@cloverridgetech.com" bg="green.500" color="white" _hover={{ bg: 'green.600' }}>
      Contact Clover Ridge
    </Button>
  </Shell>
);

const SignAgreement = () => {
  const { token } = useParams();

  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [form, setForm] = useState({ signer_name: '', signer_title: '', signer_email: '' });
  const [consent, setConsent] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const [decliningOpen, setDecliningOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [declined, setDeclined] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    apiService.contracts
      .get(token)
      .then((res) => {
        if (!active) return;
        setAgreement(res.data);
        // Pre-fill from the record so the client only has to confirm.
        setForm({
          signer_name: res.data.signer_name || res.data.client_name || '',
          signer_title: res.data.signer_title || res.data.client_title || '',
          signer_email: res.data.client_email || '',
        });
      })
      .catch((err) => {
        if (!active) return;
        setLoadError({
          status: err.response?.status,
          detail: err.response?.data?.detail || '',
        });
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token]);

  const executed = agreement?.status === 'executed';

  const dateRange = useMemo(() => {
    if (!agreement) return '';
    const { start_date: start, end_date: end } = agreement;
    if (!start && !end) return '';
    return `${fmtDate(start) || 'TBD'} — ${fmtDate(end) || 'TBD'}`;
  }, [agreement]);

  const validate = () => {
    const errors = {};
    if (form.signer_name.trim().split(/\s+/).filter(Boolean).length < 2) {
      errors.signer_name = 'Please enter your full legal name.';
    }
    if (form.signer_email && !validateEmail(form.signer_email)) {
      errors.signer_email = 'Please enter a valid email address.';
    }
    if (!consent) {
      errors.consent = 'Please confirm the statement above before signing.';
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSign = async () => {
    setSubmitError('');
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await apiService.contracts.sign(token, {
        signer_name: form.signer_name.trim(),
        signer_title: form.signer_title.trim(),
        signer_email: form.signer_email.trim(),
        consent: true,
        // Echoing the hash we rendered proves we signed the text we displayed.
        document_hash: agreement.document_hash,
      });
      setAgreement(res.data.agreement);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setSubmitError(handleApiError(err, 'We could not record your signature. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = async () => {
    setSubmitting(true);
    try {
      await apiService.contracts.decline(token, declineReason.trim());
      setDeclined(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setSubmitError(handleApiError(err, 'We could not record your response. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  // ── Load / error states ───────────────────────────────────────────────────
  if (loading) {
    return (
      <Shell>
        <Spinner color="green.500" size="xl" />
        <Text color="gray.400">Loading your agreement…</Text>
      </Shell>
    );
  }

  if (loadError) {
    if (loadError.status === 410) {
      return (
        <Notice emoji="🗄" title="This agreement was withdrawn">
          {loadError.detail || 'This document is no longer available for signature.'}
        </Notice>
      );
    }
    return (
      <Notice emoji="🔍" title="We couldn't find that agreement">
        This signing link isn't valid. Check that you copied the whole link from your email — or
        reply to that email and we'll send you a fresh one.
      </Notice>
    );
  }

  if (declined) {
    return (
      <Notice emoji="✉️" title="Thanks for letting us know">
        We've recorded that you're not ready to sign {agreement.reference}. Someone from Clover Ridge
        will follow up shortly.
      </Notice>
    );
  }

  const expired = agreement.is_expired && !executed;

  return (
    <Box py={{ base: 12, md: 20 }} px={4}>
      <VStack spacing={{ base: 8, md: 10 }} maxW="3xl" mx="auto" align="stretch">
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <VStack spacing={4} textAlign="center">
          {executed && (
            <Box w={16} h={16} mx="auto" borderRadius="full" bg="green.500" display="flex" alignItems="center" justifyContent="center" fontSize="3xl">
              ✓
            </Box>
          )}
          <Heading size={{ base: 'lg', md: 'xl' }} textTransform="uppercase" letterSpacing="wide">
            {executed ? 'Signed & executed' : 'Statement of Work'}
          </Heading>
          <Text color="gray.300" fontSize={{ base: 'md', md: 'lg' }}>
            {agreement.title}
          </Text>
          <HStack spacing={3} flexWrap="wrap" justify="center">
            <Badge bg="gray.700" color="green.300">
              {agreement.reference}
            </Badge>
            {executed ? (
              <Badge bg="green.600" color="white">
                Executed {fmtDateTime(agreement.signed_at)} ET
              </Badge>
            ) : expired ? (
              <Badge bg="red.600" color="white">
                Link expired
              </Badge>
            ) : (
              <Badge bg="yellow.600" color="white">
                Awaiting your signature
              </Badge>
            )}
          </HStack>
        </VStack>

        {executed && (
          <Box bg="green.900" border="1px solid" borderColor="green.500" borderRadius="md" p={6}>
            <Text color="white" lineHeight="1.8">
              Thank you, {agreement.signer_name?.split(' ')[0]}. This agreement is fully executed and a
              countersigned PDF has been emailed to you for your records.
            </Text>
            {agreement.pdf_url && (
              <Button
                as="a"
                href={agreement.pdf_url}
                target="_blank"
                rel="noopener noreferrer"
                mt={4}
                bg="green.500"
                color="white"
                _hover={{ bg: 'green.600' }}
              >
                Download signed PDF
              </Button>
            )}
          </Box>
        )}

        {expired && (
          <Box bg="gray.900" border="1px solid" borderColor="red.500" borderRadius="md" p={6}>
            <Text color="white" lineHeight="1.8">
              This signing link expired on {fmtDateTime(agreement.expires_at)} ET. Reply to the email
              you received and we'll send a new one — the terms below haven't changed.
            </Text>
          </Box>
        )}

        {/* ── Key terms ──────────────────────────────────────────────────── */}
        <Box bg="gray.900" border="1px solid" borderColor="gray.700" borderRadius="md" p={{ base: 5, md: 7 }}>
          <VStack align="start" spacing={3}>
            <Fact label="Between">Clover Ridge Technologies LLC</Fact>
            <Fact label="And">{agreement.client_company || agreement.client_name}</Fact>
            <Fact label="Engagement">{agreement.engagement_type_display}</Fact>
            <Fact label="Compensation">{agreement.value_display}</Fact>
            {agreement.timeline && <Fact label="Timeline">{agreement.timeline}</Fact>}
            {dateRange && <Fact label="Start / End">{dateRange}</Fact>}
            <Fact label="Acceptance window">{agreement.acceptance_window_days} business days</Fact>
          </VStack>
        </Box>

        {/* ── Body ───────────────────────────────────────────────────────── */}
        {agreement.summary && (
          <Box w="100%">
            <Heading size="sm" color="green.400" textTransform="uppercase" letterSpacing="wide" mb={3}>
              Overview
            </Heading>
            <Text color="gray.200" lineHeight="1.8" whiteSpace="pre-line">
              {agreement.summary}
            </Text>
          </Box>
        )}

        <Section title="Scope of Work" html={agreement.scope_html} />
        <Section title="Out of Scope" html={agreement.out_of_scope_html} />
        <Section title="Assumptions" html={agreement.assumptions_html} />
        <Section title="Client Responsibilities" html={agreement.client_responsibilities_html} />

        {/* ── Deliverables & DOD ─────────────────────────────────────────── */}
        {agreement.deliverables?.length > 0 && (
          <Box w="100%">
            <Heading size="sm" color="green.400" textTransform="uppercase" letterSpacing="wide" mb={2}>
              Deliverables &amp; Definition of Done
            </Heading>
            <Text color="gray.400" fontSize="sm" mb={5} lineHeight="1.7">
              Each deliverable is complete when every item below is met. You have{' '}
              {agreement.acceptance_window_days} business days after delivery to accept it or send us
              changes in writing.
            </Text>
            <VStack spacing={4} align="stretch">
              {agreement.deliverables.map((d, index) => (
                <Box
                  key={d.id}
                  bg="gray.900"
                  border="1px solid"
                  borderColor="gray.700"
                  borderRadius="md"
                  p={{ base: 5, md: 6 }}
                >
                  <HStack justify="space-between" align="start" mb={d.description ? 2 : 3}>
                    <Heading size="sm" color="white">
                      {index + 1}. {d.title}
                    </Heading>
                    {d.due_date && (
                      <Badge bg="gray.700" color="green.300" whiteSpace="nowrap">
                        {fmtDate(d.due_date)}
                      </Badge>
                    )}
                  </HStack>
                  {d.description && (
                    <Text color="gray.300" fontSize="sm" lineHeight="1.7" mb={3}>
                      {d.description}
                    </Text>
                  )}
                  {d.acceptance_criteria?.length > 0 && (
                    <>
                      <Text color="green.400" fontSize="xs" textTransform="uppercase" letterSpacing="wide" mb={2}>
                        Done when
                      </Text>
                      <VStack align="start" spacing={2}>
                        {d.acceptance_criteria.map((criterion, i) => (
                          <HStack key={i} align="start" spacing={3}>
                            <Box
                              mt="0.35rem"
                              w="0.75rem"
                              h="0.75rem"
                              flexShrink={0}
                              border="1px solid"
                              borderColor="green.500"
                              borderRadius="2px"
                            />
                            <Text color="gray.200" fontSize="sm" lineHeight="1.7">
                              {criterion}
                            </Text>
                          </HStack>
                        ))}
                      </VStack>
                    </>
                  )}
                </Box>
              ))}
            </VStack>
          </Box>
        )}

        {agreement.payment_terms && (
          <Box w="100%">
            <Heading size="sm" color="green.400" textTransform="uppercase" letterSpacing="wide" mb={3}>
              Payment Terms
            </Heading>
            <Text color="gray.200" lineHeight="1.8" whiteSpace="pre-line">
              {agreement.payment_terms}
            </Text>
          </Box>
        )}

        <Section title="Terms and Conditions" html={agreement.terms_html} />

        <Divider borderColor="gray.700" />

        {/* ── Signature ──────────────────────────────────────────────────── */}
        {executed ? (
          <Box bg="gray.900" border="1px solid" borderColor="gray.700" borderRadius="md" p={{ base: 5, md: 7 }}>
            <Heading size="sm" color="green.400" textTransform="uppercase" letterSpacing="wide" mb={4}>
              Signature record
            </Heading>
            <VStack align="start" spacing={3}>
              <Fact label="Signed by">
                {agreement.signer_name}
                {agreement.signer_title ? `, ${agreement.signer_title}` : ''}
              </Fact>
              <Fact label="Signed at">{fmtDateTime(agreement.signed_at)} ET</Fact>
            </VStack>
            <Text color="gray.500" fontSize="xs" mt={4} lineHeight="1.7">
              {agreement.consent_text}
            </Text>
          </Box>
        ) : expired ? null : (
          <Box
            bg="gray.900"
            border="1px solid"
            borderColor="green.700"
            borderRadius="md"
            p={{ base: 5, md: 7 }}
          >
            <Heading size="md" color="white" mb={2}>
              Sign this agreement
            </Heading>
            <Text color="gray.400" fontSize="sm" mb={6} lineHeight="1.7">
              Type your full legal name below. This is your electronic signature — no printing or
              scanning needed.
            </Text>

            <VStack spacing={4} align="stretch">
              <Box>
                <Text color="gray.400" fontSize="sm" mb={2}>
                  Full legal name
                </Text>
                <Input
                  value={form.signer_name}
                  onChange={(e) => setForm({ ...form, signer_name: e.target.value })}
                  placeholder="Jane Q. Client"
                  bg="black"
                  border="1px solid"
                  borderColor={fieldErrors.signer_name ? 'red.500' : 'gray.700'}
                  color="white"
                  _hover={{ borderColor: 'green.500' }}
                  _focus={{ borderColor: 'green.500', boxShadow: 'none' }}
                />
                {fieldErrors.signer_name && (
                  <Text color="red.400" fontSize="xs" mt={1}>
                    {fieldErrors.signer_name}
                  </Text>
                )}
              </Box>

              <HStack spacing={4} align="start" flexDirection={{ base: 'column', md: 'row' }}>
                <Box w="100%">
                  <Text color="gray.400" fontSize="sm" mb={2}>
                    Title <Text as="span" color="gray.600">(optional)</Text>
                  </Text>
                  <Input
                    value={form.signer_title}
                    onChange={(e) => setForm({ ...form, signer_title: e.target.value })}
                    placeholder="Owner"
                    bg="black"
                    border="1px solid"
                    borderColor="gray.700"
                    color="white"
                    _hover={{ borderColor: 'green.500' }}
                    _focus={{ borderColor: 'green.500', boxShadow: 'none' }}
                  />
                </Box>
                <Box w="100%">
                  <Text color="gray.400" fontSize="sm" mb={2}>
                    Email for your copy
                  </Text>
                  <Input
                    type="email"
                    value={form.signer_email}
                    onChange={(e) => setForm({ ...form, signer_email: e.target.value })}
                    bg="black"
                    border="1px solid"
                    borderColor={fieldErrors.signer_email ? 'red.500' : 'gray.700'}
                    color="white"
                    _hover={{ borderColor: 'green.500' }}
                    _focus={{ borderColor: 'green.500', boxShadow: 'none' }}
                  />
                  {fieldErrors.signer_email && (
                    <Text color="red.400" fontSize="xs" mt={1}>
                      {fieldErrors.signer_email}
                    </Text>
                  )}
                </Box>
              </HStack>

              {/* Signature preview — makes the typed name feel like a signature. */}
              {form.signer_name.trim() && (
                <Box bg="black" border="1px dashed" borderColor="gray.700" borderRadius="md" px={5} py={4}>
                  <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="wide" mb={1}>
                    Your signature
                  </Text>
                  <Text
                    color="white"
                    fontSize={{ base: 'xl', md: '2xl' }}
                    fontStyle="italic"
                    fontFamily="Georgia, 'Times New Roman', serif"
                  >
                    /s/ {form.signer_name.trim()}
                  </Text>
                </Box>
              )}

              <Box>
                <Checkbox
                  isChecked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  colorScheme="green"
                  alignItems="start"
                  sx={{ '.chakra-checkbox__control': { marginTop: '0.25rem' } }}
                >
                  <Text color="gray.300" fontSize="sm" lineHeight="1.7">
                    {agreement.consent_text}
                  </Text>
                </Checkbox>
                {fieldErrors.consent && (
                  <Text color="red.400" fontSize="xs" mt={1}>
                    {fieldErrors.consent}
                  </Text>
                )}
              </Box>

              {submitError && (
                <Box bg="red.900" border="1px solid" borderColor="red.500" borderRadius="md" p={4}>
                  <Text color="white" fontSize="sm">
                    {submitError}
                  </Text>
                </Box>
              )}

              <Button
                onClick={handleSign}
                isLoading={submitting}
                loadingText="Recording your signature…"
                bg="green.500"
                color="white"
                size="lg"
                _hover={{ bg: 'green.600' }}
              >
                Sign &amp; execute agreement
              </Button>

              <Text color="gray.600" fontSize="xs" textAlign="center" lineHeight="1.7">
                We record the date, time, and IP address of your signature as part of the audit trail
                for this contract.
              </Text>
            </VStack>
          </Box>
        )}

        {/* ── Decline ────────────────────────────────────────────────────── */}
        {!executed && !expired && (
          <Box textAlign="center">
            {decliningOpen ? (
              <VStack spacing={4} align="stretch">
                <Text color="gray.400" fontSize="sm">
                  Not ready to sign? Let us know what needs to change.
                </Text>
                <Textarea
                  value={declineReason}
                  onChange={(e) => setDeclineReason(e.target.value)}
                  placeholder="What would you like adjusted?"
                  bg="black"
                  border="1px solid"
                  borderColor="gray.700"
                  color="white"
                  rows={3}
                  _hover={{ borderColor: 'green.500' }}
                  _focus={{ borderColor: 'green.500', boxShadow: 'none' }}
                />
                <HStack justify="center" spacing={3}>
                  <Button variant="ghost" color="gray.400" onClick={() => setDecliningOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    variant="outline"
                    borderColor="gray.600"
                    color="gray.200"
                    isLoading={submitting}
                    onClick={handleDecline}
                    _hover={{ borderColor: 'red.500', color: 'red.300' }}
                  >
                    Send response
                  </Button>
                </HStack>
              </VStack>
            ) : (
              <Button variant="link" color="gray.500" fontSize="sm" onClick={() => setDecliningOpen(true)}>
                I'm not ready to sign this
              </Button>
            )}
          </Box>
        )}

        <Text color="gray.600" fontSize="xs" textAlign="center" lineHeight="1.7">
          Questions? Email{' '}
          <a href="mailto:hello@cloverridgetech.com" style={{ color: '#3FA66A' }}>
            hello@cloverridgetech.com
          </a>
          . Clover Ridge Technologies LLC.
        </Text>
      </VStack>
    </Box>
  );
};

export default SignAgreement;
